/**
 * Resolves the connection bindings a step must carry in a target workflow context.
 *
 * Steps travel between contexts (cross-workflow paste, workflow duplication, import
 * into an integration) with whatever `credentialId` / `integrationAppId` their
 * source carried, and neither survives the move:
 *
 * - Integration workflows resolve credentials per installation — provisioning for the
 *   integration's embedded app, `integrationAppId` for connector apps. A company
 *   `credentialId` is never valid there: it binds every end user's run to the
 *   partner's own connection (or fails "No Connection Found" when it does not
 *   resolve), and the editor has no surface that shows or clears it.
 * - Workflows without an integration have no integration apps, so `integrationAppId`
 *   is always cleared; `credentialId` is left alone because company connections are
 *   the only credential source there.
 *
 * In an integration workflow a binding is kept when it belongs to the target
 * integration; otherwise the step is bound to the integration's app for its `appId`
 * when exactly one exists. No match, or several apps for the same `appId`, resolves
 * to null so the editor asks for an explicit pick instead of guessing.
 *
 * Pure. `integrationApps` are the target integration's `embedded.integration_app`
 * rows (`{ id, appId }`).
 */
export const resolveStepConnectionBindings = ({ step, integrationId, integrationApps = [] }) => {
  if (!integrationId) {
    return { credentialId: step?.credentialId ?? null, integrationAppId: null };
  }

  const currentBinding = step?.integrationAppId || null;
  if (currentBinding && integrationApps.some((app) => app?.id === currentBinding)) {
    return { credentialId: null, integrationAppId: currentBinding };
  }

  const matchingApps = integrationApps.filter((app) => !!step?.appId && app?.appId === step.appId);

  return {
    credentialId: null,
    integrationAppId: matchingApps.length === 1 ? matchingApps[0].id : null
  };
};
